import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';

/** Every reason the booking routes give for there being nothing to book. */
export type BookMeetingUnavailableCode =
    | 'enquiriesClosed'
    | 'sessionDisabled'
    | 'noConsultant'
    | 'notFound'
    | 'loadFailed';

interface UnavailableCopy {
    heading: string;
    body: (professionalName: string) => string;
    /** Whether an enquiry is a sensible way on; not when enquiries are the thing that is off. */
    offersEnquiry: boolean;
}

const COPY: Record<BookMeetingUnavailableCode, UnavailableCopy> = {
    enquiriesClosed: {
        heading: 'Not taking meetings yet',
        body: (name) =>
            `${name} isn’t taking meeting requests online at the moment.`,
        offersEnquiry: false,
    },
    sessionDisabled: {
        heading: 'This booking link is paused',
        body: (name) =>
            `${name} has paused bookings for this meeting. Send them an enquiry instead and they’ll suggest a time.`,
        offersEnquiry: true,
    },
    noConsultant: {
        heading: 'No one is free to book',
        body: (name) =>
            `Nobody at ${name} can be booked online right now. Send them an enquiry and they’ll suggest a time.`,
        offersEnquiry: true,
    },
    notFound: {
        heading: 'This meeting can’t be found',
        body: (name) =>
            `The booking link may have been removed. Send ${name} an enquiry instead.`,
        offersEnquiry: true,
    },
    loadFailed: {
        heading: 'We couldn’t load meeting times',
        body: () =>
            'Something went wrong on our side. Please try again in a moment.',
        offersEnquiry: false,
    },
};

export interface BookMeetingGateUnavailableProps {
    code: BookMeetingUnavailableCode;
    /** The route's own wording, used where the code is not one the gate knows. */
    message?: string;
    professionalName: string;
    /** Hands the reader to the enquiry gate. Omitted where there is nothing to hand to. */
    onEnquireInstead?: () => void;
    onClose?: () => void;
}

/**
 * Why a meeting cannot be booked here, and the way on when there is one.
 *
 * Every refusal the booking routes give lands on this rather than a blank
 * calendar: the reader came to reach the practice, so the answer says who is
 * not taking meetings and offers an enquiry wherever one can still be sent.
 */
export const BookMeetingGateUnavailable = ({
    code,
    message,
    professionalName,
    onEnquireInstead,
    onClose,
}: BookMeetingGateUnavailableProps) => {
    const copy = COPY[code] as UnavailableCopy | undefined;
    const showEnquire =
        Boolean(onEnquireInstead) && (copy?.offersEnquiry ?? false);

    return (
        <div
            className="mx-auto my-auto flex h-full w-full max-w-90.75 flex-col"
            role="status"
        >
            <GateHeading className="mt-3">
                {copy?.heading ?? 'Not taking meetings yet'}
            </GateHeading>
            <GateSubcopy>{copy?.body(professionalName) ?? message}</GateSubcopy>
            <div className="flex-1" />
            {showEnquire || onClose ? (
                <GateStickyFooter className="mt-8" desktopClassName="mb-2">
                    {showEnquire ? (
                        <button
                            type="button"
                            onClick={onEnquireInstead}
                            className={GATE_CTA_CLASS}
                        >
                            Send an enquiry
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={onClose}
                            className={GATE_CTA_CLASS}
                        >
                            Close
                        </button>
                    )}
                </GateStickyFooter>
            ) : null}
        </div>
    );
};
